import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  isLegacyApprovalReady,
  isLegacyListingRequired,
  legacyListingRequiredMessage,
} from "./trainerLegacyEbayReviewUtils";

const STATUS_CONFIG = {
  ready_to_update: { label: "Ready to update", color: "success" },
  ready_to_relist: { label: "Ready to relist", color: "warning" },
  inventory_not_applied: { label: "Legacy local stock state", color: "default" },
  allocation_not_applied: { label: "Legacy local allocation state", color: "default" },
  no_allocation: { label: "No allocation", color: "default" },
  already_applied: { label: "Already applied", color: "success" },
  recovered_applied: { label: "Recovered - already on eBay", color: "success" },
  missing_linked_listing: { label: "Listing required - review", color: "warning" },
  missing_sku: { label: "Missing SKU", color: "warning" },
  lookup_failed: { label: "Lookup failed", color: "error" },
  live_lookup_failed: { label: "Lookup failed", color: "error" },
  live_quantity_untrusted: { label: "Quantity untrusted", color: "error" },
  updated_successfully: { label: "Updated successfully", color: "success" },
  pushed: { label: "Updated successfully", color: "success" },
  relisted_successfully: { label: "Relisted successfully", color: "success" },
  relisted: { label: "Relisted successfully", color: "success" },
  update_failed: { label: "Update failed", color: "error" },
  push_failed: { label: "Update failed", color: "error" },
  relist_failed: { label: "Relist failed", color: "error" },
  manual_review_required: { label: "Manual review", color: "default" },
  manual_review: { label: "Manual review", color: "default" },
  missing_ebay_identifier: { label: "Manual review", color: "default" },
};

const EMPTY_VALUE = "-";

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return EMPTY_VALUE;
  }
  return value;
};

const actionKey = (rowId, listingRole) => `${rowId}:${listingRole}`;
const isReadyStatus = isLegacyApprovalReady;
const isRelistStatus = (status) => ["ready_to_relist", "relisted", "relisted_successfully", "relist_failed"].includes(status);

const getStatusConfig = (status) =>
  STATUS_CONFIG[status] || {
    label: status ? String(status).replace(/_/g, " ") : "Not refreshed",
    color: "default",
  };

const StatusChip = ({ status }) => {
  const config = getStatusConfig(status);
  return <Chip size="small" color={config.color} label={config.label} />;
};

const formatQuantitySource = (projection) => {
  if (!projection?.ebay_quantity_source) {
    return EMPTY_VALUE;
  }
  if (projection.ebay_quantity_source === "trading_get_item_available_quantity") {
    return "Trading GetItem";
  }
  if (projection.ebay_quantity_source === "sell_inventory_offer") {
    return "Offer API";
  }
  return String(projection.ebay_quantity_source).replace(/_/g, " ");
};

const quantityWarningText = (projection) => {
  if (!projection) {
    return "";
  }
  if (isLegacyListingRequired(projection)) {
    return legacyListingRequiredMessage(projection);
  }
  if (projection.status === "ready_to_relist") {
    return "Approval republishes the existing ended or unpublished offer. No duplicate offer is created.";
  }
  if (projection.status === "inventory_not_applied" || projection.status === "allocation_not_applied") {
    return projection.message || "Legacy local bookkeeping state; refresh again under the eBay-authoritative Bulk Intake flow.";
  }
  if (projection.status === "live_quantity_untrusted") {
    return projection.message || "Live eBay quantity could not be verified. Approval is blocked.";
  }
  if (projection.status === "recovered_applied") {
    return projection.message || "eBay already has the previously attempted quantity. No second push is required.";
  }
  if (projection.retry_state === "ambiguous") {
    return projection.message || "Current eBay quantity differs from the known retry baseline and target. Manual review is required.";
  }
  if (projection.ebay_quantity_mismatch) {
    return "Offer API quantity differed from the trusted Seller Hub quantity source. The trusted quantity is used.";
  }
  return projection.message || "";
};

const actionLabel = (projection) => (projection?.status === "ready_to_relist" ? "Relist" : "Approve");

export default function TrainerIntakeEbayReconciliationPanel({
  rows,
  reconciliationByRowId,
  selectedRowId,
  actionStates,
  onSelectRow,
  onRefreshRow,
  onApproveRow,
  onApplyAllReady,
}) {
  const actions = rows.flatMap((row) => {
    const singleAllocation = Number(row.single_allocation_quantity || 0);
    const playsetAllocation = Number(row.playset_allocation_quantity || 0);
    const next = [];
    if (singleAllocation > 0) {
      next.push({ row, listingRole: "single", allocationQuantity: singleAllocation });
    }
    if (playsetAllocation > 0) {
      next.push({ row, listingRole: "playset", allocationQuantity: playsetAllocation });
    }
    return next;
  });

  const readyActions = actions.filter(({ row, listingRole }) =>
    isReadyStatus(reconciliationByRowId[actionKey(row.id, listingRole)]?.status)
  );
  const selectedActions = actions.filter(({ row }) => String(row.id) === String(selectedRowId));
  const applyAllBusy = Boolean(actionStates?.applyAll);

  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        gap={1}
        sx={{ p: 2, pb: 1 }}
      >
        <Box>
          <Typography variant="subtitle1">eBay Quantity Reconciliation</Typography>
          <Typography variant="caption" color="text.secondary">
            Trusted current eBay quantity is the listing baseline. Each role adds only this intake row's Single/Playset increment, then requires explicit approval before eBay is changed.
          </Typography>
        </Box>
        <Tooltip title="Push every listing-role action currently ready to update or relist">
          <span>
            <Button
              size="small"
              variant="contained"
              startIcon={<DoneAllIcon />}
              onClick={onApplyAllReady}
              disabled={readyActions.length === 0 || applyAllBusy}
            >
              Apply All Ready Actions
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {applyAllBusy && <LinearProgress />}

      {selectedActions.length > 0 && (
        <Alert severity="info" icon={false} sx={{ mx: 2, mb: 1 }}>
          Selected row #{selectedRowId}: {selectedActions.map(({ listingRole, allocationQuantity }) => `${listingRole} +${allocationQuantity}`).join(" | ")}. Refresh trusted eBay quantity to calculate the proposed listing quantity.
        </Alert>
      )}

      <TableContainer sx={{ maxHeight: 460 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Card</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell>Listing</TableCell>
              <TableCell align="right">Cached Before</TableCell>
              <TableCell align="right">Live eBay</TableCell>
              <TableCell>Qty Source</TableCell>
              <TableCell align="right">Intake +</TableCell>
              <TableCell align="right">Proposed / Target</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {actions.map(({ row, listingRole, allocationQuantity }) => {
              const key = actionKey(row.id, listingRole);
              const projection = reconciliationByRowId[key] || {};
              const refreshBusy = actionStates?.refreshing?.[key];
              const approvingBusy = actionStates?.approving?.[key];
              const isReady = isReadyStatus(projection.status);
              const isRelist = isRelistStatus(projection.status);
              const warningText = quantityWarningText(projection);

              return (
                <TableRow
                  key={key}
                  hover
                  selected={String(row.id) === String(selectedRowId)}
                  onClick={() => onSelectRow(row.id)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>{row.card_name}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={listingRole === "single" ? "Single" : "Playset x4"} />
                  </TableCell>
                  <TableCell>{formatValue(projection.sku)}</TableCell>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <Typography variant="body2">
                        {projection.listing_id ? `Listing #${projection.listing_id}` : formatValue(projection.linked_listing_id)}
                      </Typography>
                      {(projection.ebay_offer_id || projection.ebay_listing_id) && (
                        <Typography variant="caption" color="text.secondary">
                          {projection.ebay_offer_id ? `Offer ${projection.ebay_offer_id}` : ""}
                          {projection.ebay_offer_id && projection.ebay_listing_id ? " | " : ""}
                          {projection.ebay_listing_id ? `eBay ${projection.ebay_listing_id}` : ""}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{formatValue(projection.app_quantity_before_refresh)}</TableCell>
                  <TableCell align="right">{formatValue(projection.refreshed_live_ebay_quantity)}</TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography variant="caption">{formatQuantitySource(projection)}</Typography>
                      {warningText && (
                        <Typography variant="caption" color={isRelist ? "warning.main" : "text.secondary"}>
                          {warningText}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    +{allocationQuantity}{listingRole === "playset" ? ` (${allocationQuantity * 4} cards)` : ""}
                  </TableCell>
                  <TableCell align="right">
                    <Stack spacing={0.25} alignItems="flex-end">
                      <Typography variant="body2">{formatValue(projection.relist_quantity ?? projection.projected_quantity)}</Typography>
                      {projection.local_target_after_refresh !== null && projection.local_target_after_refresh !== undefined && (
                        <Typography variant="caption" color="text.secondary">
                          Local target (info) {projection.local_target_after_refresh}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <StatusChip status={projection.status} />
                      {isRelist && <Typography variant="caption" color="warning.main">Existing offer relist</Typography>}
                      {isLegacyListingRequired(projection) && (
                        <Typography variant="caption" color="warning.main">
                          Link existing listing or create an internal draft. No eBay approval yet.
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right" onClick={(event) => event.stopPropagation()}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title={`Refresh trusted live eBay quantity for the ${listingRole} listing`}>
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={() => onRefreshRow(row.id, listingRole)}
                            disabled={refreshBusy || approvingBusy || applyAllBusy}
                          >
                            Refresh
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip title={projection.status === "ready_to_relist" ? "Republish the existing offer with projected quantity" : "Approve and push projected quantity to eBay"}>
                        <span>
                          <Button
                            size="small"
                            variant="contained"
                            color={projection.status === "ready_to_relist" ? "warning" : "success"}
                            startIcon={<CheckCircleIcon />}
                            onClick={() => onApproveRow(row.id, listingRole)}
                            disabled={!isReady || refreshBusy || approvingBusy || applyAllBusy}
                          >
                            {actionLabel(projection)}
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            {actions.length === 0 && (
              <TableRow>
                <TableCell colSpan={11}>No single or playset allocations in this batch require eBay reconciliation.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
